// Module-level single-slot registry for the command-palette
// rich-preview pane (CON-183).
//
// The unified slideover's inline-edit hooks
// (`useDealUpdate`, `useContactUpdate`, `useStageMove`,
// `useContactSwap`, the contact assignees row) write their
// optimistic patch back through `router.replaceProp("drawer")`.
// When the SAME entry bodies are rendered inside the palette
// preview pane there is no URL-driven `drawer` prop to patch —
// the data lives in the pane's local React state. This
// registry bridges that gap: the pane registers an `updater`
// while it's mounted, and the prop-update helpers route their
// `"preview"` propKey to `applyPreviewUpdate` instead of
// Inertia's page store.
//
// Single-slot is correct: only one palette preview is ever
// open at a time (the palette mounts one `DrawerPreviewPane`).
// The pane sets the updater on mount / when its data loads and
// clears it (`setPreviewUpdater(null)`) on unmount, so a stale
// updater can never outlive the pane.

type PreviewUpdater = (transformer: (data: unknown) => unknown) => void

let updater: PreviewUpdater | null = null

// Register (or clear) the active preview pane's updater. The
// pane passes a function that applies the transformer to its
// local preview data; pass `null` on unmount.
export function setPreviewUpdater(fn: PreviewUpdater | null) {
  updater = fn
}

// Apply an optimistic transform to the active preview's data.
// A no-op when no preview pane is mounted (the inline-edit
// hooks call this unconditionally for the `"preview"` propKey;
// outside a preview there's simply nothing to patch).
export function applyPreviewUpdate(t: (data: unknown) => unknown) {
  updater?.(t)
}
